import React, { useEffect, useState } from "react";
import { X, AlertTriangle } from "lucide-react";

// Ported from halco-ops-v10.jsx so ported tabs keep their exact markup and
// class names — the CSS is byte-identical, so the DOM has to match it.

export function Modal({ title, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="ovl" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={"mdl" + (wide ? " wide" : "")}>
        <div className="mdl-h">
          <h3>{title}</h3>
          <button className="iconbtn x" onClick={onClose} aria-label="Close">
            <X size={19} />
          </button>
        </div>
        <div className="mdl-b">{children}</div>
        {footer && <div className="mdl-f">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Two-step delete: first click arms, second fires, and it disarms itself after
 * four seconds. Kept from v10 — it needs no dialog, which matters because the
 * original ran in a sandboxed frame where window.confirm was blocked.
 */
export function ConfirmButton({ label, confirmLabel, onConfirm, className, title, style, disabled }) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      title={armed ? "Click again to confirm" : title}
      style={armed
        ? { ...(style || {}), background: "var(--red)", color: "#fff", borderColor: "var(--red)", fontWeight: 700 }
        : style}
      onClick={(e) => {
        e.stopPropagation();
        if (armed) { setArmed(false); onConfirm(); } else setArmed(true);
      }}
    >
      {armed ? (confirmLabel || "Click again to confirm") : label}
    </button>
  );
}

export function Fld({ label, hint, full, err, children }) {
  return (
    <div className={"fld" + (full ? " full" : "") + (err ? " err" : "")}>
      {label && <label>{label}</label>}
      {children}
      {err && <div className="errnote"><AlertTriangle size={13} /> {err}</div>}
      {hint && !err && <div className="hint">{hint}</div>}
    </div>
  );
}

export function Chip({ kind, children }) {
  return <span className={"chip " + kind}>{children}</span>;
}

export function EmptyState({ title, sub, action }) {
  return (
    <div className="empty">
      <b>{title}</b>
      <div>{sub}</div>
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

/**
 * Loading and error states, which v10 had no need for — its data was already in
 * memory. Every table now waits on a network round trip, and an error here is
 * usually an RLS policy rather than a network fault, so the message says so.
 */
export function Loading({ what = "data" }) {
  return <div className="empty"><b>Loading…</b><div>Fetching {what}.</div></div>;
}

export function ErrorState({ error, onRetry }) {
  return (
    <div className="empty">
      <b>Could not load</b>
      <div style={{ maxWidth: 460, margin: "0 auto" }}>{String(error?.message ?? error)}</div>
      <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--muted)" }}>
        If this says nothing useful, it is usually a row level security policy
        rather than a network problem — an unauthorised read returns empty, not an error.
      </div>
      {onRetry && (
        <div style={{ marginTop: 14 }}>
          <button className="btn ghost" onClick={onRetry}>Try again</button>
        </div>
      )}
    </div>
  );
}
